import * as Sentry from "@sentry/browser";
import { getNodesBounds, getViewportForBounds, type Node } from "@xyflow/react";
import { toBlob } from "html-to-image";
import { DateTime } from "luxon";
import type { ShowErrorToastFn } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import { type Site } from "../../../utils/backend-types";
import { uploadFileOrFiles } from "../../../utils/files/uploadFileOrFiles";
import {
  STRUCTURE_VIEW_IMAGE_HEIGHT,
  STRUCTURE_VIEW_IMAGE_WIDTH
} from "../../StructureViewFlowDiagram/StructureViewFlowDiagram";
import { StructureViewMode } from "../StructureView.constants";
import type { MkExportRequest, MkExportResponse } from "../StructureView.types";

/* This function triggers the creation of the metering concept document for todo 3.1 */
function createMeteringConceptPdf(
  showErrorToast: ShowErrorToastFn,
  siteDetails: Site | undefined,
  siteWizardId: number,
  structureViewMode: StructureViewMode,
  updateStructureViewMode: (newValue: StructureViewMode) => void,
  getNodes: () => Array<Node>
) {
  const modeSave = structureViewMode;
  if (modeSave !== StructureViewMode.MeteringConcept) {
    updateStructureViewMode(StructureViewMode.MeteringConcept);
  }
  // the timeout is needed to wait for the structure view to update
  setTimeout(() => {
    generateBlobImage(getNodes)
      .then((blob) => {
        if (!blob) {
          showErrorToast("Fehler bei der Ablage des Messkonzept-Reports");
        } else {
          const currentDate = DateTime.now().toFormat("yyyyLLdd_HHmmss");
          const image = new File(
            [blob],
            getFileName(siteDetails, currentDate, "pdf"),
            { type: blob.type }
          );

          uploadFileOrFiles<MkExportResponse, Omit<MkExportRequest, "image">>(
            image,
            urls.apiWithoutCamelization.mkExport(),
            "image",
            {
              site: siteWizardId,
              upload_to_todo: true
            },
            "POST",
            true
          ).catch((error: unknown) => {
            showErrorToast("Fehler bei der Ablage des Messkonzept-Reports");
            Sentry.captureException(error);
          });
        }
      })
      .catch(() => {
        showErrorToast("Fehler bei der Ablage des Messkonzept-Reports");
      })
      .finally(() => {
        if (modeSave !== StructureViewMode.MeteringConcept) {
          updateStructureViewMode(modeSave);
        }
      });
  }, 1000);
}

export function generateBlobImage(
  getNodes: () => Array<Node>
): Promise<null | Blob> {
  const nodesBounds = getNodesBounds(getNodes());
  //Sometimes the bounds are too small and names are cut off, so we increase the width by 15%
  nodesBounds.width += nodesBounds.width * 0.15;
  const viewport = getViewportForBounds(
    nodesBounds,
    STRUCTURE_VIEW_IMAGE_WIDTH,
    STRUCTURE_VIEW_IMAGE_HEIGHT,
    0.5,
    2,
    0.1
  );
  const reactFlowViewport = document.querySelector<HTMLElement>(
    ".react-flow__viewport"
  );

  if (reactFlowViewport) {
    return toBlob(reactFlowViewport, {
      backgroundColor: "white",
      width: STRUCTURE_VIEW_IMAGE_WIDTH,
      height: STRUCTURE_VIEW_IMAGE_HEIGHT,
      quality: 1,
      style: {
        width: STRUCTURE_VIEW_IMAGE_WIDTH.toString(),
        height: STRUCTURE_VIEW_IMAGE_HEIGHT.toString(),
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`
      }
    });
  }

  return Promise.resolve(null);
}

export function getFileName(
  siteDetails: Site | undefined,
  currentDate: string,
  extension: "pdf" | "png"
) {
  return `${siteDetails?.name ?? "Unbekannt"}_${currentDate}.${extension}`;
}

export { createMeteringConceptPdf };
